import { Injectable } from '@angular/core';

import { IObjectStringKeyMap } from '@shared/models';
import { Events } from '@services/events/events.service';
import { CompareValueComponent, InUseConditionComponent } from '@shared/components';
import { CompareValue, OutputValue } from '@shared/components/compare-value/model';
import { IncrementEntryFieldsModel } from '@modules/management/pages/details/check/models';
import { ITreeFolder } from '@modules/management/modules/folders/model/folders.interfaces';
import {
  Asset,
  IAssetPropertyValue,
  IPropertyValueZone,
  PropertyActivity
} from '@services/assets/asset.interfaces';
import {
  AssetsService,
  AssetState,
  CommsService,
  FoldersDataService,
  FormField,
  ISelectMenuItem,
  Module,
  Permission,
  PermissionsService,
  SubscriberService,
  UserdataService,
  UserService,
  UtilsService
} from '@services';
import {
  IProperty,
  IPropertyEvent,
  IPropertyEventActivity,
  IPropertyEventDurationValue,
  IPropertyEventMeasurementValue,
  IPropertyEventRequestsParams,
  IPropertyEventValue,
  IPropertyMeta,
  LimitType,
  PropertyCategory,
  PropertyMethod,
  PropertySource,
  PropertyStyle,
  PropertySubject,
  ThresholdType
} from './property-model.interfaces';
import { IN_USE_STATUSES, inUseState } from './in-use-state';

import { MarkdownService } from 'ngx-markdown';
import { TranslateService } from '@ngx-translate/core';
import {
  cloneDeep,
  flatten,
  isArray,
  indexOf,
  each,
  filter,
  find,
  includes,
  intersection,
  isUndefined,
  map,
  some,
  isObject,
  last,
  has,
  isNumber, reject, isEmpty, isNull
} from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  public readonly propertyNumberTypes = [
    PropertyCategory.Numeric,
    PropertyCategory.MissedCheck,
    PropertyCategory.SkippedCheck,
    PropertyCategory.CompletedCheck,
    PropertyCategory.FailedCheck,
    PropertyCategory.PassedCheck,
    PropertyCategory.OpenObservations,
    PropertyCategory.UnassignedObservations
  ];
  public aiStatuses = {
    [AssetState.Down]: {
      type: 'red',
      title: 'PROPERTY.State_Down'
    },
    [AssetState.Strained]: {
      type: 'yellow',
      title: 'PROPERTY.State_Strained'
    },
    [AssetState.Up]: {
      type: 'green',
      title: 'PROPERTY.State_Up'
    }
  };

  private properties = {
    lastRequest: null,
    data: {} as IObjectStringKeyMap<IProperty>
  };

  constructor(
    private commsService: CommsService,
    private events: Events,
    private subscriberService: SubscriberService,
    private permissionService: PermissionsService,
    private userData: UserdataService,
    private translate: TranslateService,
    private utils: UtilsService,
    private markdownService: MarkdownService,
    private assetsService: AssetsService,
    private foldersDataService: FoldersDataService,
    private userService: UserService,
  ) {}

  public refresh(): Promise<IObjectStringKeyMap<IProperty>> {
    const requestData: any = {
      cmd: 'getProperties',
      lastRequest: this.properties.lastRequest,
    };

    return this.commsService.sendMessage(requestData, false, false).then((response) => {
      if (response?.reqStatus === 'OK') {
        this.updateCache(response);
      }

      return this.properties.data;
    });
  }

  public updateCache(data) {
    this.properties.data = data.result.properties;
    this.properties.lastRequest = data.result.timestamp;
    this.events.publish('ccs:propertiesUpdate', true);
  }

  public getPropertiesAsList(includeInactive: boolean = false, includeDisabled: boolean = false, folderID?: number, sifter?: (item: IProperty) => boolean): IProperty[] {
    const theList: IProperty[] = [];

    each(this.properties.data, (item) => {
      if (!isUndefined(folderID) && (item.folderID !== folderID)) {
        return;
      }

      if (!includeInactive && !item.active) {
        return;
      }
      if (!includeDisabled && item.disabledAt) {
        return;
      }
      if (!isUndefined(sifter) && !sifter(item)) {
        return;
      }
      theList.push(item);
    });

    return theList;
  }

  public getPropertiesByCategory(categories: PropertyCategory | PropertyCategory[]) {
    const properties = this.getPropertiesAsList();
    categories = flatten([categories]);

    return filter(properties, (property) => {
      return includes(categories, property.category);
    });
  }

  public isNumericProperty(propertyId: number) {
    const property = this.getPropertyById(propertyId);

    return includes(this.propertyNumberTypes, property?.category);
  }

  public getPropertyValuesFields(fieldPrefix: string, fieldConfig?: FormField): FormField[] {
    let properties = this.getPropertiesByCategory([...this.propertyNumberTypes, PropertyCategory.InService, PropertyCategory.InUse]);

    properties = filter<IProperty>(properties, (property) => {
      return property.category === PropertyCategory.InService && includes([PropertyStyle.UpDown, PropertyStyle.UpStrainedDown], property.style) ||
        property.category !== PropertyCategory.InService;
    });

    return map(properties, (property) => {
      let title = this.folderPath(property);
      let propertyTitle = this.getPropertyName(property);
      title = `${title ? title + '/' : ''}${propertyTitle}`

      return {
        title,
        name: `${fieldPrefix}${property.propertyID}`,
        canDelete: true,
        required: true,
        ...this.getPropertyFieldExtension(property),
        ...(fieldConfig || {})
      } as FormField;
    });
  }

  public getPropertyName(property: IProperty | number, includeUnit?: boolean): string {
    if (isNumber(property)) {
      property = this.getPropertyById(property);
    }
    if (!property) {
      return '';
    }

    let ret = this.userService.translateItem(property, 'title');
    if (includeUnit && property?.unit) {
      // if a unit is defined, attach it to the title in parentheses
      const unit = this.getPropertyUnit(property);
      if (!isEmpty(unit)) {
        ret += ` (${unit})`;
      }
    }
    return ret;
  }

  public getPropertyUnit(property: IProperty | number): string {
    if (isNumber(property)) {
      property = this.getPropertyById(property);
    }
    if (PropertyCategory.Numeric !== property.category && PropertyCategory.InService !== property.category) {
      return '';
    }

    let ret = property?.unit || '';
    if (!isEmpty(ret)) {
      if (PropertyStyle.Measurement === property.style) {
        // this is a measurement - use the pre-defined units
        ret = this.getMeasurementTitle(property.unit);
      } else {
        ret = this.userService.translateItem(property, 'unit');
      }
    }
    return ret;
  }

  private getPropertyFieldExtension(property: IProperty): FormField {
    if (property.category === PropertyCategory.InService) {
      const stateOptions = {
        up: {
          id: '2',
          description: this.translate.instant('PROPERTY.State_Up')
        },
        strained: {
          id: '1',
          description: this.translate.instant('PROPERTY.State_Strained')
        },
        down: {
          id: '0',
          description: this.translate.instant('PROPERTY.State_Down')
        }
      };

      const optionsByStyle = {
        [PropertyStyle.UpStrainedDown]: [stateOptions.up, stateOptions.strained, stateOptions.down],
        [PropertyStyle.UpDown]: [stateOptions.up, stateOptions.down],
      };

      return {
        type: 'checkbox',
        class: 'inline-checkboxes',
        multiple: true,
        role: 'none',
        options: optionsByStyle[property.style]
      };
    } else if (property.category === PropertyCategory.InUse) {
      return {
        type: 'customElement',
        component: InUseConditionComponent
      };
    } else {
      return {
        type: 'customElement',
        component: CompareValueComponent
      };
    }
  }

  /**
   * The form value for a saved `propertyValues_<id>` rule / deployment condition. An inList
   * condition becomes its values array (the inService checkboxes), except on an asset:inUse
   * property, whose field takes the whole condition so its minDuration survives a reopen.
   */
  public decodePropertyCondition(fieldName: string, condition: OutputValue): OutputValue | OutputValue['values'] {
    const propertyID = +fieldName.replace(/^\D+/, '');
    if (condition?.id === CompareValue.InList && !this.isInUse(propertyID)) {
      return condition?.values ?? [];
    }
    return condition;
  }

  /**
   * The saved condition for a `propertyValues_<id>` form value, or null when there is none. Condition
   * fields post JSON ({ id, ... }); the inService checkboxes post their ticked ids - an array, or a
   * single id - which becomes { id: 'inList', values }.
   */
  public encodePropertyCondition(fieldName: string, value: unknown): OutputValue | null {
    const propertyID = +fieldName.replace(/^\D+/, '');
    if (this.getPropertyById(propertyID)?.category === PropertyCategory.InService) {
      const values = flatten([value]).filter((v) => v !== undefined && v !== null && v !== '');
      return values.length ? { id: CompareValue.InList, values: values as string[] } : null;
    }

    let condition = value;
    if (typeof value === 'string') {
      try {
        condition = JSON.parse(value);
      } catch {
        condition = null;
      }
    }
    return isObject(condition) && !isArray(condition) && (condition as OutputValue).id ? condition as OutputValue : null;
  }

  public getFilteredVisibleProperties(ids: IPropertyMeta[], overriddenIds: IPropertyMeta[]): IProperty[] {
    return reject(this.getPropertiesByIds(ids), (property) => this.isPropertyHiddenFor(property, overriddenIds)) as IProperty[];
  }

  // The asset's selectedProperties override wins over the property's own hidden flag. CVX-6583: the
  // stored propertyID is often a string ("969"), so match it numerically.
  public isPropertyHiddenFor(property: IProperty, overriddenIds: IPropertyMeta[] = []): boolean {
    const selectedProperty = find(overriddenIds, (selection) => +selection.propertyID === property.propertyID);

    if (selectedProperty && 'hidden' in selectedProperty) {
      return !!selectedProperty.hidden;
    }

    return this.isPropertyHidden(property);
  }

  public isPropertyHidden(property: IProperty): boolean {
    const folder = this.foldersDataService.getFolderByID(property.folderID);
    return !!(folder?.hidden || folder?.attributes?.hidden || property.hidden);
  }

  public getPropertiesByIds(ids: IPropertyMeta[]): IProperty[] {
    return filter(map(ids, (id) => this.getPropertyById(id.propertyID)));
  }

  /**
   * Expand selectedProperties array (which can include propertyFolderID) to actual property IDs
   * Uses hasIn() to check if properties belong to selected property folders
   */
  public expandSelectedPropertiesToIds(selectedProperties: any[], folderTree: IObjectStringKeyMap<ITreeFolder>): number[] {
    const propertyIds: number[] = [];

    selectedProperties?.forEach(selection => {
      if (selection.propertyID) {
        // Direct property selection
        propertyIds.push(selection.propertyID);
      } else if ('propertyFolderID' in selection) {
        const propertyFolderID = (selection as any).propertyFolderID;
        if (propertyFolderID === 0) {
          // Special case: "All Properties"
          const allProperties = this.getPropertiesAsList();
          allProperties.forEach(p => propertyIds.push(p.propertyID));
        } else {
          // Get all properties in this folder and its descendants
          const allProperties = this.getPropertiesAsList();
          allProperties.forEach(property => {
            if (this.foldersDataService.hasIn(propertyFolderID, property.folderID, folderTree)) {
              propertyIds.push(property.propertyID);
            }
          });
        }
      }
    });

    return propertyIds;
  }

  public getProperties(): IObjectStringKeyMap<IProperty> {
    return this.properties.data;
  }

  public getPropertyEvents(propertyID: number, subjectID: number | string, subject: PropertySubject, activity?: string, date?: { startTime: number, endTime: number }): Promise<IPropertyEvent[]> {
    let requestData: IObjectStringKeyMap<any> = {
      cmd: 'getPropertyEvents',
      propertyID,
      subjectID,
      subject,
      activity
    };

    if (date) {
      requestData.startTime = date.startTime;
      requestData.endTime = date.endTime;
    }

    return this.commsService.sendMessage(requestData, false, false).then((response) => {
      if (response?.reqStatus === 'OK') {
        return response?.result?.events || [];
      }
    });
  }

  public async getPropertyWithEvents(propertyID: number, limit?: number): Promise<IProperty> {
    const requestData: IObjectStringKeyMap<any> = {
      cmd: 'getProperties',
      propertyID,
      includeEvents: 1
    };

    if (limit) {
      requestData.limit = limit;
    }

    return this.commsService.sendMessage(requestData, false, false).then((response) => {
      if (response?.reqStatus === 'OK') {
        if (has(response?.result?.properties, propertyID)) {
          return response.result.properties[propertyID];
        } else {
          return this.getPropertyById(propertyID);
        }
      }
    });
  }

  public getPropertyById(id: number): IProperty {
    return cloneDeep(this.properties.data[id]);
  }

  public folderPath(property: IProperty): string {
    let propertyPath = '';

    if (property.folderID) {
      const folderPath = this.foldersDataService.getFolderPath(property.folderID);

      each(folderPath, (path) => {
        propertyPath = `/${path}${propertyPath}`;
      });
    }

    return propertyPath;
  }

  public addProperty(data: any): Promise<any> {
    const requestData = {
      cmd: 'addProperty',
      ...data
    };
    return this.commsService.sendMessage(requestData, false, false);
  }

  public async updateProperty(data: any): Promise<any> {
    const requestData = {
      cmd: 'updateProperty',
      ...data
    };
    const result = await this.commsService.sendMessage(requestData, false, false);
    await this.refresh();
    return result;
  }

  public deleteProperty(ids: number[] | number | string): Promise<any> {
    const requestData = {
      cmd: 'deleteProperty',
      properties: flatten([ids])
    };

    return this.commsService.sendMessage(requestData, false, false);
  }

  public capturePropertyEvents(params: IPropertyEventRequestsParams): Promise<any> {
    const requestData = {
      cmd: 'capturePropertyEvents',
      sendTime: Date.now(),
      ...params
    };

    return this.commsService.sendMessage(requestData, false, false);
  }

  public getLimitTypeList(): ISelectMenuItem[] {
    return [
      { id: LimitType.Healthy, description: 'PROPERTY.Healthy'},
      { id: LimitType.Strained, description: 'PROPERTY.Strained'},
    ];
  }
  public getThresholdTypeList(category?: PropertyCategory): ISelectMenuItem[] {
    const ret = [];
    if (category === PropertyCategory.InService || category === PropertyCategory.ContentAvailable) {
      ret.push(
        { id: ThresholdType.Selector, description: 'PROPERTY.Selector'},
      );
    } else if (category === PropertyCategory.ReapprovalDate || category === PropertyCategory.ReviewDate) {
      // this is something that will be reapproved or renewed; the thresholds need to be dates
      ret.push({ id: ThresholdType.Interval, description: 'PROPERTY.TimeUntil'});
    } else {
    ret.push (
      { id: ThresholdType.Toggle, description: 'PROPERTY.Boolean'},
      { id: ThresholdType.Ascending, description: 'PROPERTY.Ascending'},
      { id: ThresholdType.Descending, description: 'PROPERTY.Descending'},
    );
    }
    return ret;
  }

  public getCalculationMethodList(limit?: PropertySource): ISelectMenuItem[] {
    const ret = [];
    if (!limit) {
      ret.push(
      { id: PropertyMethod.Latest, description: 'PROPERTY.Latest'},
      { id: PropertyMethod.Highest, description: 'PROPERTY.Highest'},
      { id: PropertyMethod.Lowest, description: 'PROPERTY.Lowest'},
      { id: PropertyMethod.Cumulative, description: 'PROPERTY.Cumulative'},
      { id: PropertyMethod.Average, description: 'PROPERTY.Average'},
      );
    } else {
      if (limit === PropertySource.Calculated) {
        ret.push(
          { id: PropertyMethod.Latest, description: 'PROPERTY.Latest'},
        );
      } else if (limit === PropertySource.Derived) {
        ret.push(
          { id: PropertyMethod.Cumulative, description: 'PROPERTY.Cumulative'},
        );
      } else if (limit === PropertySource.OverTime) {
        ret.push(
          { id: PropertyMethod.Cumulative, description: 'PROPERTY.Cumulative'},
        );
      } else if (limit === PropertySource.PushOverTime) {
        ret.push(
        { id: PropertyMethod.Latest, description: 'PROPERTY.Latest'},
        { id: PropertyMethod.Highest, description: 'PROPERTY.Highest'},
        { id: PropertyMethod.Lowest, description: 'PROPERTY.Lowest'},
        { id: PropertyMethod.Cumulative, description: 'PROPERTY.Cumulative'},
        { id: PropertyMethod.Average, description: 'PROPERTY.Average'},
        );
      } else {
      ret.push(
        { id: PropertyMethod.Latest, description: 'PROPERTY.Latest'},
        { id: PropertyMethod.Highest, description: 'PROPERTY.Highest'},
        { id: PropertyMethod.Lowest, description: 'PROPERTY.Lowest'},
        { id: PropertyMethod.Cumulative, description: 'PROPERTY.Cumulative'},
        { id: PropertyMethod.Average, description: 'PROPERTY.Average'},
      );
      }
    }
    return ret;
  }

  public getSourceList(limit?: string): ISelectMenuItem[] {
    const ret = [];
    if (!limit) {
      ret.push(
        { id: PropertySource.Manual, description: 'PROPERTY.Manual' },
        { id: PropertySource.Hybrid, description: 'PROPERTY.Hybrid' },
        { id: PropertySource.Derived, description: 'PROPERTY.Derived' },
        { id: PropertySource.TimeUntil, description: 'PROPERTY.TimeUntil' },
        { id: PropertySource.OverTime, description: 'PROPERTY.OverTime' },
        { id: PropertySource.Pull, description: 'PROPERTY.Pull' },
        { id: PropertySource.Push, description: 'PROPERTY.Push' },
        { id: PropertySource.PushOverTime, description: 'PROPERTY.PushOverTime' },
      );
    } else {
      if (limit.match(/^check:/)) {
        ret.push({ id: PropertySource.OverTime, description: 'PROPERTY.OverTime' });
      } else if (limit.match(/^content:/)) {
        ret.push({ id: PropertySource.TimeUntil, description: 'PROPERTY.TimeUntil' });
      } else if (limit.match(/^observation:/)) {
        ret.push({ id: PropertySource.Derived, description: 'PROPERTY.Derived' });
      } else if (limit === PropertyCategory.Zone) {
        ret.push({ id: PropertySource.Push, description: 'PROPERTY.Push' });
      } else {
        ret.push(
          { id: PropertySource.Manual, description: 'PROPERTY.Manual' },
          { id: PropertySource.Hybrid, description: 'PROPERTY.Hybrid' },
          { id: PropertySource.Push, description: 'PROPERTY.Push' },
          { id: PropertySource.PushOverTime, description: 'PROPERTY.PushOverTime' },
          { id: PropertySource.Calculated, description: 'PROPERTY.Calculated' }
        );
      }
    }

    return ret;
  }
  public getStyleList(limit?: string): ISelectMenuItem[] {
    const ret = [];
    if (!limit) {
    ret.push (
      { id: PropertyStyle.Numeric, description: 'PROPERTY.Numeric' },
      { id: PropertyStyle.Text, description: 'PROPERTY.Text' },
      { id: PropertyStyle.TextArea, description: 'PROPERTY.TextArea' },
      { id: PropertyStyle.Note, description: 'PROPERTY.Note' },
      { id: PropertyStyle.Flipswitch, description: 'PROPERTY.Flipswitch' },
      { id: PropertyStyle.Duration, description: 'PROPERTY.Duration' },
      { id: PropertyStyle.Timestamp, description: 'MGMT_DETAILS.Date' },
      { id: PropertyStyle.Measurement, description: 'PROPERTY.Measurement' },
      { id: PropertyStyle.Counter, description: 'PROPERTY.Counter' }
    );
    } else if (limit.match(/^content:/)) {
      ret.push ({ id: PropertyStyle.Numeric, description: 'PROPERTY.Numeric' });
    } else if (limit.match(/^check:/)) {
      ret.push ({ id: PropertyStyle.Numeric, description: 'PROPERTY.Numeric' });
    } else if (limit.match(/^observation:/)) {
      ret.push ({ id: PropertyStyle.Numeric, description: 'PROPERTY.Numeric' });
    } else if (limit === PropertyCategory.InService) {
      ret.push (
        { id: PropertyStyle.UpDown, description: 'PROPERTY.UpDown' },
        { id: PropertyStyle.UpStrainedDown, description: 'PROPERTY.UpStrainedDown' },
      );
    } else if (limit === PropertyCategory.ContentAvailable || limit === PropertyCategory.InUse) {
      ret.push (
        { id: PropertyStyle.UpDown, description: 'PROPERTY.UpDown' },
      );
    } else if (limit === PropertyCategory.Numeric) {
      ret.push (
        { id: PropertyStyle.Numeric, description: 'PROPERTY.Numeric' },
        { id: PropertyStyle.Measurement, description: 'PROPERTY.Measurement' },
        // { id: PropertyStyle.Counter, description: 'PROPERTY.Counter' }
      );
    } else if (limit === PropertyCategory.Text) {
      ret.push (
        { id: PropertyStyle.Text, description: 'PROPERTY.Text' },
        { id: PropertyStyle.TextArea, description: 'PROPERTY.TextArea' },
        // { id: PropertyStyle.Note, description: 'PROPERTY.Note' }
      );
    }
    return ret;
  }

  public getCategoryList(value?: string[] | string): ISelectMenuItem[] {
    if (isUndefined(value)) {
      value = [];
    } else if ( !isArray(value)) {
      value = [ value ];
    }

    const cats = [];
    if (!value.length || indexOf(value, PropertySubject.Asset) > -1) {
      cats.push(
        { id: PropertyCategory.InService, description: 'PROPERTY.InService'},
        { id: PropertyCategory.InUse, description: 'PROPERTY.InUse'}
      );

      if (this.subscriberService.usesModule(Module.CHECKS)) {
        cats.push(
          { id: PropertyCategory.CompletedCheck, description: 'PROPERTY.CompletedCheck'},
          { id: PropertyCategory.MissedCheck, description: 'PROPERTY.MissedCheck'},
          { id: PropertyCategory.SkippedCheck, description: 'PROPERTY.SkippedCheck'},
          { id: PropertyCategory.PassedCheck, description: 'PROPERTY.PassedCheck'},
          { id: PropertyCategory.FailedCheck, description: 'PROPERTY.FailedCheck'},
        );
      }

      if (this.subscriberService.usesModule(Module.OBSERVATIONS)) {
        cats.push(
          { id: PropertyCategory.OpenObservations, description: 'PROPERTY.OpenObservations'},
          { id: PropertyCategory.UnassignedObservations, description: 'PROPERTY.UnassignedObservations'}
        );
      }

      cats.push({
        id: PropertyCategory.Zone,
        description: 'SHARED.Zone'
      });
    }

    if (!value.length || indexOf(value, PropertySubject.Content) > -1) {
      cats.push(
        // { id: PropertyCategory.ContentAvailable, description: 'CONTENT.IsAvailable'},
        { id: PropertyCategory.ReapprovalDate, description: 'CONTENT.ReapprovalComing'},
        { id: PropertyCategory.ReviewDate, description: 'CONTENT.ReviewDate'},
        // { id: PropertyCategory.ReviewsCompleted, description: 'CONTENT.ReviewsCompleted'},
        // { id: PropertyCategory.ReviewsMissing, description: 'CONTENT.ReviewsMissing'}
      );
    }

    if (!value.length || indexOf(value, PropertySubject.Content) === -1) {
      cats.push(
        { id: PropertyCategory.Numeric, description: 'PROPERTY.Numeric'},
        { id: PropertyCategory.Text, description: 'PROPERTY.Text'},
        { id: PropertyCategory.Timestamp, description: 'SHARED.Timestamp'}
      );
    }

    return cats;
  }

  public getSubjectList(): ISelectMenuItem[] {
    const subjects = [
      {
        id: PropertySubject.Content,
        description: 'CONTENT.Module_Name'
      },
      {
        id: PropertySubject.Asset,
        description: 'SHARED.Asset'
      },
      // {
      //   id: PropertySubject.Worker, // TODO [azyulikov] will be implemented in next release
      //   description: 'SHARED.User'
      // },
      // {
      //   id: PropertySubject.Zone,
      //   description: 'SHARED.Zone'
      // },
      // {
      //   id: PropertySubject.Location,
      //   description: 'SHARED.Location'
      // }
    ];
    return subjects;
  }

  public getCategoryName(category: PropertyCategory): string {
    let ret = 'SHARED.None';

    switch (category) {
      case PropertyCategory.Text: {
        ret = 'PROPERTY.Text';
        break;
      }
      case PropertyCategory.Numeric: {
        ret = 'PROPERTY.Numeric';
        break;
      }
      case PropertyCategory.InService: {
        ret = 'PROPERTY.InService';
        break;
      }
      case PropertyCategory.InUse: {
        ret = 'PROPERTY.InUse';
        break;
      }
      case PropertyCategory.MissedCheck: {
        ret = 'PROPERTY.MissedCheck';
        break;
      }
      case PropertyCategory.SkippedCheck: {
        ret = 'PROPERTY.SkippedCheck';
        break;
      }
      case PropertyCategory.CompletedCheck: {
        ret = 'PROPERTY.CompletedCheck';
        break;
      }
      case PropertyCategory.FailedCheck: {
        ret = 'PROPERTY.FailedCheck';
        break;
      }
      case PropertyCategory.PassedCheck: {
        ret = 'PROPERTY.PassedCheck';
        break;
      }
      case PropertyCategory.OpenObservations: {
        ret = 'PROPERTY.OpenObservations';
        break;
      }
      case PropertyCategory.UnassignedObservations: {
        ret = 'PROPERTY.UnassignedObservations';
        break;
      }
      case PropertyCategory.Zone: {
        ret = 'PROPERTY.Zone';
        break;
      }
      case PropertyCategory.Timestamp: {
        ret = 'PROPERTY.TimeStamp';
        break;
      }
      case PropertyCategory.ReapprovalDate: {
        ret = 'CONTENT.ReapprovalComing';
        break;
      }
      // case PropertyCategory.ReviewsCompleted: {
      //   ret = 'CONTENT.ReviewsCompleted';
      //   break;
      // }
      // case PropertyCategory.ReviewsMissing: {
      //   ret = 'CONTENT.ReviewsMissing';
      //   break;
      // }
      case PropertyCategory.ReviewDate: {
        ret = 'CONTENT.ReviewDate';
        break;
      }
      case PropertyCategory.ContentAvailable: {
        ret = 'CONTENT.IsPublished';
        break;
      }
    }
    return ret;
  }

  public isEventHistoryAvailable(property: IProperty): boolean {
    return includes([PropertySource.Push, PropertySource.Manual, PropertySource.Derived, PropertySource.Hybrid, PropertySource.TimeUntil], property.source);
  }

  public canAddManualPropertyEvent(property: IProperty): boolean {
    return (this.canViewManualProperty(property) || this.isInService(property)) &&
      this.isUserAllowedToAddEntry(property) &&
      !!property?.manualInput?.addEvent?.enabled;
  }

  public canViewManualProperty(property: IProperty): boolean {
    return includes([PropertyCategory.Numeric, PropertyCategory.Text, PropertyCategory.Timestamp], property?.category) &&
      this.isManualSource(property);
  }

  public isManualSource(property: IProperty): boolean {
    return includes([PropertySource.Manual, PropertySource.Hybrid], property?.source);
  }

  public isUserAllowedToAddEntry(property: IProperty) {
    const { permissions, teams, users, types } = (property?.manualInput?.addEvent?.permissions || {});
    let isValid = true;

    if (users?.length && includes(users, this.userData.userID)) {
      isValid = true;
    }

    if (isValid && permissions?.length) {
      isValid = some(permissions, (permission) => {
        return this.permissionService.canView(permission);
      })
    }

    if (isValid && teams?.length) {
      isValid = intersection(teams, this.userData.teams).length > 0;
    }

    if (isValid && types?.length) {
      isValid = includes(types, this.userData.type);
    }

    return isValid || this.permissionService.canView(Permission.Corvex);
  }

  public isInService(property: IProperty): boolean {
    return this.isManualSource(property) && property?.category === PropertyCategory.InService;
  }

  public isInUse(property: IProperty | number): boolean {
    const theProperty = isNumber(property) ? this.getPropertyById(property) : property;
    return theProperty?.category === PropertyCategory.InUse;
  }

  /** The In Use / Not In Use badge colour and (customer-overridable) term for a reading. */
  public getInUseStatus(reading: unknown): { type: string; title: string } {
    return IN_USE_STATUSES[inUseState(reading)];
  }

  public getPropertyActivity(activity: PropertyActivity | IPropertyEventActivity) {
    const activityMap = {
      [PropertyActivity.Calculated]: 'PROPERTY.Calculated',
      [PropertyActivity.Manual]: 'PROPERTY.Manual',
      [PropertyActivity.InRange]: 'PROPERTY.In_Range',
      [PropertyActivity.OutOfRange]: 'PROPERTY.Out_Of_Range'
    };

    if (activityMap[activity as PropertyActivity]) {
      return this.translate.instant(activityMap[activity as PropertyActivity]);
    } else {
      let activityValue  = activity as string;
      try {
        const activityObject: IPropertyEventActivity = JSON.parse(activity as string);
        activityValue = `${activityObject.type}: ${activityObject.from}`;
      } catch (e) {}

      return activityValue;
    }
  }

  public getEventNumberValue(event: IAssetPropertyValue, propertyID: number): number {
    const result = this.getPropertyValue(event, propertyID);

    if (isNumber(+result) && !isNaN(+result)) {
      return +result;
    }
  }

  public getPrecision()  {
    return [
      { id: -1, description: 'PROPERTY.Full_Precision'},
      { id: 0, description: '0'},
      { id: 1, description: '0.1'},
      { id: 2, description: '0.12'},
      { id: 3, description: '0.123'},
      { id: 4, description: '0.1234'},
      { id: 5, description: '0.12345'},
      { id: 6, description: '0.123456'},
      { id: 7, description: '0.1234567'},
      { id: 8, description: '0.12345678'},
    ];
  }

  public getPropertyValue(propertyValue: IAssetPropertyValue, propertyID: number, cellType = 'display') {
    let value = propertyValue?.value;
    const style = (value as IPropertyEventValue)?.style;
    const activity = propertyValue?.activity;

    if (includes([PropertyActivity.InRange, PropertyActivity.OutOfRange], activity)) {
      return (value as IPropertyValueZone)?.rssi;
    } else if (activity === PropertyActivity.Calculated) {
      return value;
    } else {
      // the 1 / 0 state, not its label: callers hand this to displayPropertyValue (which
      // translates) or read it as a number, and a label would not read back as a state
      if (this.isInUse(propertyID)) {
        return inUseState(value);
      } else if (this.isHealthPropertyValue(propertyValue, propertyID)) {
        return this.getPropertyHealthValue(propertyValue, propertyID);
      } else if (style) {
        return this.getManualEntryPropertyValue(value as IPropertyEventValue, cellType);
      } else {
        const eventValue = value as IPropertyEventValue;
        return eventValue?.value ?? eventValue;
      }
    }
  }

  public getPropertyHealthValue(propertyValue: IAssetPropertyValue, propertyID: number, stateValue = false) {
    let value = propertyValue?.value;

    if (this.isHealthPropertyValue(propertyValue, propertyID)) {
      const healthValue = (value as IPropertyEventValue)?.value ?? value;
      const status = this.getAIStatusByValue(+healthValue);

      if (stateValue) {
        return status ? +healthValue : null;
      } else {
        return status ? this.translate.instant(status?.title) : healthValue;
      }
    }
  }

  public isHealthPropertyValue(propertyValue: IAssetPropertyValue, propertyID: number) {
    const property = this.getPropertyById(propertyID);
    // an asset:inUse reading is a 1 / 0 state, never a health value, even with the upDown style
    return property?.category !== PropertyCategory.InUse && this.isUpDownValue(propertyValue) && property?.healthRelated;
  }

  public isInServiceUpDownValue(propertyValue: IAssetPropertyValue, propertyID: number): boolean {
    const property = this.getPropertyById(propertyID);
    const isInServiceCategory = property.category === PropertyCategory.InService;

    return this.isUpDownValue(propertyValue) && isInServiceCategory;
  }

  private isUpDownValue(propertyValue: IAssetPropertyValue) {
    const value = (propertyValue as IAssetPropertyValue)?.value ?? propertyValue;
    const style = (value as IPropertyEventValue)?.style;
    const isPrimitiveWithoutStyle = !isObject(value) && !style && AssetState[value];
    const isUpStrainedStyle = includes([PropertyStyle.UpDown, PropertyStyle.UpStrainedDown], style);

    return isPrimitiveWithoutStyle || isUpStrainedStyle;
  }


  public getTableCellValue(valueObject: IPropertyEventValue | string, cellType = 'display'): string {
    // `?? valueObject` here would resolve back to the event object whenever the
    // inner .value is null or absent, which is how an object reached the
    // exporter and wrote [object Object] into the sheet. Unwrap to a scalar or
    // to nothing -- never back to the object (CVX-6376).
    const value = isObject(valueObject) ? (valueObject as IPropertyEventValue).value : valueObject;
    const hasValue = !isNull(value) && !isUndefined(value) && value !== '';
    // Only look up a status when there is a value to look up: +'' and +null are
    // both 0 and AssetState.Down is 0, so an absent value would otherwise
    // export the "Down" label as though it had been recorded.
    const status = hasValue ? this.getAIStatusByValue(+value) : undefined;

    if (cellType === 'display') {
      return this.getTableCellHealth(valueObject) || ((valueObject as IPropertyEventValue).value || valueObject) as string;
    } else if (cellType === 'export') {
      // Never hand back valueObject: the `as string` was a lie. It is
      // IPropertyEventValue | string, and structured {style, value, note}
      // values are real production data, so an exporter stringifies them to
      // [object Object] (CVX-6376 -- the same defect CVX-6374 fixed for the
      // Asset Issues status columns). Export the label the badge shows.
      return status ? this.translate.instant(status.title) : (hasValue ? value : '') as string;
    } else {
      return status?.type || ((hasValue ? value : '') as string);
    }
  }

  private getTableCellHealth(valueObject: IPropertyEventValue | string) {
    const value = (valueObject as IPropertyEventValue)?.value ?? valueObject;
    const status = this.getAIStatusByValue(+value);

    if (status?.type) {
      return `
        <div class="property-status">
          <span class="observation-circle ${status.type}"></span>
          <span class="property-value">${this.translate.instant(status.title)}</span>
        </div>
      `;
    }
  }

  public getManualEntryPropertyValue(valueObject: IPropertyEventValue, cellType = 'display') {
    if (!valueObject) {
      return '';
    }

    const style = valueObject.style;

    if (style === PropertyStyle.Measurement) {
      const measurementData = (valueObject as IPropertyEventMeasurementValue);
      const measurementByUnits = measurementData?.[measurementData?.units];
      return measurementByUnits ? `${measurementData[measurementData.units]} ${this.getMeasurementTitle(measurementData.units)}` : valueObject?.note;
    } else if (includes([PropertyStyle.Date, PropertyStyle.Timestamp], style)) {
      return this.utils.dateTimeFormat(+valueObject.value / 1000, null, false, true);
    } else if (style === PropertyStyle.Duration) {
      const durationData = (valueObject as IPropertyEventDurationValue);
      return `${durationData.value} ${durationData.value_unit}`;
    } else if (style === PropertyStyle.Flipswitch) {
      return (valueObject as IPropertyEventValue).value ? this.translate.instant('SHARED.Yes') : this.translate.instant('SHARED.No');
    } else if (style === PropertyStyle.TextArea) {
      // Markdown renders to HTML, which an exporter would write into the cell
      // verbatim. An export wants the text the author typed (CVX-6376).
      return cellType === 'export'
        ? valueObject.value.toString()
        : this.markdownService.parse(valueObject.value.toString());
    }

    // Extract the actual value, handling nested objects
    let extractedValue = valueObject?.value ?? valueObject?.note ?? valueObject;
    
    // If the extracted value is still an object, try to get its value property
    if (isObject(extractedValue) && has(extractedValue, 'value')) {
      extractedValue = extractedValue['value'];
    }
    
    // Convert to string, ensuring we don't return [Object Object]
    return isObject(extractedValue) ? JSON.stringify(extractedValue) : (extractedValue?.toString() ?? '');
  }

  public getMeasurementTitle(unitId: string): string {
    const unit = find(IncrementEntryFieldsModel.UNITS, { id: unitId });
    return unit?.description ? this.translate.instant(unit?.description) : unitId;
  }

  public defineEventStatus(propertyID: number, propertyStyle: PropertyStyle, events: IPropertyEvent[], propertyEventStatusRef: IObjectStringKeyMap<string>) {
    const lastEvent = last(filter(events, (event) => {
      return (event?.value as IPropertyEventValue)?.value ?? (!isObject(event?.value) && event?.value);
    })) as IPropertyEvent;

    if (lastEvent) {
      const style = (lastEvent.value as IPropertyEventValue)?.style;
      const eventValue = lastEvent?.value;
      const propertyStyles = [PropertyStyle.UpDown, PropertyStyle.UpStrainedDown];

      if ((!isObject(lastEvent.value) && includes(propertyStyles, propertyStyle)) || includes(propertyStyles, style)) {
        const stateValue = +((lastEvent.value as IPropertyEventValue)?.value ?? lastEvent.value as string);
        propertyEventStatusRef[propertyID] = this.assetsService.assetState(stateValue);
      } else if (style) {
        propertyEventStatusRef[propertyID] = this.getManualEntryPropertyValue(lastEvent.value as IPropertyEventValue);
      } else {
        propertyEventStatusRef[propertyID] = (eventValue as IPropertyEventValue)?.value as string ?? (eventValue as IPropertyEventValue)?.note ?? eventValue as string;
      }
    }
  }

  public getAIStatusByValue(value: number) {
    return this.aiStatuses[value];
  }

  public isTrackState(asset: Asset) {
    const status = this.getAIStatusByValue(asset.health);
    return status?.type && !this.assetsService.isAssetNoState(asset);
  }

  public displayPrecision(property: IProperty | number, defaultPrecision?: number): number {
    if (!isObject(property)) {
      property = this.getPropertyById(property as number);
    }
    if (property.category === PropertyCategory.Numeric) {
      if (has (property, 'displayPrecision') ) {
        if (property.displayPrecision > -1) {
          return property.displayPrecision;
        } else {
          return defaultPrecision ?? null;
        }
      } else {
        if (!isUndefined(defaultPrecision)) {
          return defaultPrecision;
        } else {
          return null;
        }
      }
    }
    return null;
  }


  public displayPropertyValue(property: IProperty | number, value: IPropertyEventValue | string, metaItems?: IPropertyMeta[], cellType = 'display'): string {
    if (isNull(value) || isUndefined(value)) {
      return '';
    }
    if (!isObject(property)) {
      property = this.getPropertyById(property as number);
    }

    if (property.static) {
      const staticMeta = find(metaItems, { propertyID: property.propertyID });
      if (!isUndefined(staticMeta?.defaultValue)) {
        value = staticMeta.defaultValue as string;
      }
    }

    if (property.category === PropertyCategory.InUse) {
      return this.translate.instant(this.getInUseStatus(value).title);
    }

    if (property.category === PropertyCategory.Numeric && has(property, 'displayPrecision') && property.displayPrecision > -1) {
      const numericValue = +((value as IPropertyEventValue)?.value ?? value);
      if (isNumber(numericValue) && !isNaN(numericValue)) {
        return numericValue.toFixed(property.displayPrecision);
      }
    }

    if (isObject(value)) {
      // Reachable on export: getPropertyValue returns a Calculated event's value
      // object untouched, so this is the only place that unwraps it (CVX-6376).
      return this.getManualEntryPropertyValue(value as IPropertyEventValue, cellType);
    } else {
      return value as string;
    }
  }
}
